import React from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import Icon from "components/AppIcon";

const esgScores = [
  { subject: "Climate", score: 88 },
  { subject: "Biodiversity", score: 72 },
  { subject: "Social", score: 81 },
  { subject: "Governance", score: 90 },
  { subject: "Labour", score: 85 },
  { subject: "Community", score: 78 },
];

const impactMetrics = [
  { year: "2026", co2Avoided: 42, jobsCreated: 180, homesSupplied: 28000 },
  { year: "2027", co2Avoided: 85, jobsCreated: 95, homesSupplied: 56000 },
  { year: "2028", co2Avoided: 85, jobsCreated: 95, homesSupplied: 56000 },
  { year: "2029", co2Avoided: 87, jobsCreated: 98, homesSupplied: 57500 },
  { year: "2030", co2Avoided: 89, jobsCreated: 100, homesSupplied: 59000 },
];

const esgIndicators = [
  { label: "ESG Score", value: "A+", icon: "Star", color: "text-success" },
  { label: "SFDR Classification", value: "Article 9", icon: "FileCheck", color: "text-primary" },
  { label: "TCFD Aligned", value: "Yes", icon: "CheckCircle", color: "text-success" },
  { label: "UN SDGs", value: "7, 9, 11, 13", icon: "Globe", color: "text-primary" },
  { label: "Carbon Intensity", value: "12 gCO₂/kWh", icon: "Wind", color: "text-success" },
  { label: "GHG Protocol", value: "Scope 1 & 2", icon: "Leaf", color: "text-success" },
];

const sdgs = [
  { id: 7, label: "Affordable & Clean Energy", color: "bg-yellow-400" },
  { id: 9, label: "Industry, Innovation & Infrastructure", color: "bg-orange-500" },
  { id: 11, label: "Sustainable Cities", color: "bg-amber-600" },
  { id: 13, label: "Climate Action", color: "bg-green-600" },
];

export default function ESGTab() {
  return (
    <div className="space-y-6">
      {/* ESG Indicators */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">ESG Compliance Indicators</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {esgIndicators?.map((ind) => (
            <div key={ind?.label} className="p-3 rounded-md bg-muted/50">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={ind?.icon} size={14} color={`var(--color-${ind?.color?.replace("text-", "")})`} />
                <span className="font-caption text-xs text-muted-foreground">{ind?.label}</span>
              </div>
              <p className={`font-data text-base font-semibold ${ind?.color}`}>{ind?.value}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="card">
          <h3 className="font-heading text-base font-semibold text-foreground mb-4">ESG Dimension Scores</h3>
          <div className="w-full h-56" aria-label="ESG radar chart showing dimension scores">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={esgScores}>
                <PolarGrid stroke="var(--color-border)" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fontFamily: "var(--font-caption)" }} />
                <Radar name="ESG Score" dataKey="score" stroke="var(--color-primary)" fill="rgba(59,76,76,0.15)" strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CO2 Impact */}
        <div className="card">
          <h3 className="font-heading text-base font-semibold text-foreground mb-4">CO₂ Avoided (kt/year)</h3>
          <div className="w-full h-56" aria-label="CO2 avoided bar chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={impactMetrics} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "var(--font-data)" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "var(--font-data)" }} />
                <Tooltip contentStyle={{ fontFamily: "var(--font-caption)", fontSize: 12 }} />
                <Bar dataKey="co2Avoided" name="CO₂ Avoided (kt)" fill="var(--color-success)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Impact Metrics */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Projected Impact Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "CO₂ Avoided (Annual)", value: "85,000 tonnes", icon: "Wind", trend: "+2.4% YoY" },
            { label: "Homes Supplied", value: "56,000", icon: "Home", trend: "+3.6% YoY" },
            { label: "Jobs Created", value: "275 (peak)", icon: "Users", trend: "95 permanent" },
          ]?.map((m) => (
            <div key={m?.label} className="p-4 rounded-md border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={m?.icon} size={18} color="var(--color-success)" />
                <span className="font-caption text-sm text-muted-foreground">{m?.label}</span>
              </div>
              <p className="font-data text-xl font-semibold text-foreground">{m?.value}</p>
              <p className="font-caption text-xs text-success mt-1">{m?.trend}</p>
            </div>
          ))}
        </div>
      </div>
      {/* UN SDGs */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">UN Sustainable Development Goals</h3>
        <div className="flex flex-wrap gap-3">
          {sdgs?.map((sdg) => (
            <div key={sdg?.id} className={`flex items-center gap-2 px-3 py-2 rounded-md ${sdg?.color} text-white`}>
              <span className="font-data font-bold text-sm">SDG {sdg?.id}</span>
              <span className="font-caption text-xs">{sdg?.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}